import React, { Component } from 'react';
import CanvasElement, { CanvasElementPropTypes } from '../../../canvas-element';
import pick from 'lodash/pick';
import SelectedElementWrapperUtil from '../utils';
import { observer } from 'mobx-react';
import * as commonFun from '../../../common';
import GraphicContentEditor from './shape-components/graphic-content-editor';
import omit from 'lodash/omit';
import { mobxSet, omitStyle } from '@src/slide/infrastructure/module/independent/assert/get-set';
import ImageCropping from './image-components/image-croping';
import ObserverFactory from '@src/slide/application/service/modelObserver/observerFactory';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import GraphicContentEditorMobile
    from '@editor/src/page/index/components/canvas/edit-panel/selected-element-layer/elements-container/shape-components/graphic-content-editor-mobile';
import { ServicesContext } from '../../context';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import type { SelectElementWrapper } from '@src/framework/editor/src/page/index/components/canvas/edit-panel/selected-element-layer/selected-ele-wrapper-factory';

interface SelectedImageWrapperProps extends SelectElementWrapper {
    element: IPictureModel;
    scale: number;
    isCroping: boolean;
    editable: boolean;
    isPreview: boolean;
    isResizing: boolean;
    isDragging: boolean;
    isRotating: boolean;
    isMobileRotatingMode: boolean;
}

@observer
class SelectedImageWrapper extends Component<SelectedImageWrapperProps> {
    public static contextType = ServicesContext;

    public constructor(props) {
        super(props);
    }


    public componentDidMount() {
        const observer = ObserverFactory.createObserver(this.props.element);
        if (observer) {
            observer.addLocalChangeObserver(this.updateComponent);
            observer.addChangesetObserver(this.updateComponent);
        }
    }

    public updateComponent = () => {
        this.forceUpdate();
    };

    public componentWillUnmount() {
        const observer = ObserverFactory.createObserver(this.props.element);
        if (observer) {
            observer.removeLocalChangeObserver(this.updateComponent);
            observer.removeChangesetObserver(this.updateComponent);
        }
    }

    public render() {
        const elementModel = this.props.element;
        const frameInfo = SelectedElementWrapperUtil.frameInfoForInner(elementModel);
        const frameWithOp = elementModel.getFrame?.().getFrameInfo(true);
        const relativeInfo = SelectedElementWrapperUtil.relativeInfoForInner(elementModel);

        const commonProps = SelectedElementWrapperUtil
            .commonPropsForCanvasElement(elementModel, this.props.scale);

        const controlValues = elementModel.operatingGraphicInfo
            ? elementModel.operatingGraphicInfo.controlValues
            : elementModel.props.style.controlValues;
        const onlyBeSelected = this.getSelectionService()?.selectedElements.length === 1;

        return (
            <React.Fragment>
                <CanvasElement
                    {...pick(this.props, Object.keys(CanvasElementPropTypes))}
                    showSelectBorder={true}
                    scale={this.props.scale}
                    rect={{
                        ...frameWithOp,
                    }}
                    resizeHorizontal={true}
                    resizeVertical={true}
                    editable={true}
                    canRotate={true}
                    relativeRotate={relativeInfo.relativeRotate}
                    relativeFlipH={relativeInfo.relativeFlipH}
                    relativeFlipV={relativeInfo.relativeFlipV}
                    rotate={frameInfo.rotate}
                    onBorderClick={this.props.onBorderClick}
                    {
                        ...commonProps
                    }
                    isMobileRotatingMode={this.props.isMobileRotatingMode}
                >
                    {
                        this.props.isCroping
                        && <div style={{
                            width: frameWithOp.width,
                            height: frameWithOp.height,
                            transform: commonFun.transformOfFlip(frameInfo),
                            position: 'absolute',
                        }}>
                            <ImageCropping
                                element={elementModel}
                                scale={this.props.scale}
                                rect={frameWithOp}
                                rotate={frameInfo.rotate}
                                relativeRotate={relativeInfo.relativeRotate}
                                relativeFlipH={relativeInfo.relativeFlipH}
                                relativeFlipV={relativeInfo.relativeFlipV}
                            />
                        </div>
                    }
                </CanvasElement>
                {
                    onlyBeSelected && this.props.editable && !this.props.isPreview
                    && <div
                        style={{
                            width: '100%',
                            height: '100%',
                            position: 'absolute',
                            zIndex: '1000',
                            transform: commonFun.transformOfFlip(frameWithOp),
                        }}
                    >
                        {
                            (browser.isMobile) ? <GraphicContentEditorMobile
                                model={{
                                    ...omit(omitStyle(this.props.element.props.style), 'width', 'height', 'left', 'top'),
                                    ...frameWithOp,
                                    rotate: frameInfo.rotate,
                                    controlValues,
                                }}
                                scale={this.props.scale}
                                onControlValueChange={this.handleControlValuesChange}
                                onControlPointMouseDown={this.handleControlPointMouseDown}
                                onControlPointMoving={this.handleControlPointMoving}
                                isResizing={this.props.isResizing}
                                isRotating={this.props.isRotating}
                                editorIsEditing={this.getEditorInstanceService().checkEditorIsEditing()}
                                isMobileRotatingMode={this.props.isMobileRotatingMode}
                            /> : <GraphicContentEditor
                                model={{
                                    ...omit(omitStyle(this.props.element.props.style), 'width', 'height', 'left', 'top'),
                                    ...frameWithOp,
                                    rotate: frameInfo.rotate,
                                    controlValues,
                                }}
                                scale={this.props.scale}
                                onControlValueChange={this.handleControlValuesChange}
                                onControlPointMouseDown={this.handleControlPointMouseDown}
                                onControlPointMoving={this.handleControlPointMoving}
                            >
                            </GraphicContentEditor>
                        }
                    </div>
                }
            </React.Fragment>
        );
    }

    private getSelectionService() {
        return (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .viewableServiceLayer?.selectionService;
    }

    private getEditorInstanceService() {
        return (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .viewableServiceLayer.editorInstanceService;
    }

    private getBorderSketchManager() {
        return (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .editableServiceLayer?.borderSketchManager;
    }

    private handleControlValuesChange = (newControlValues) => {
        const graphicInfo = {
            controlValues: newControlValues,
        };
        if (this.props.element) {
            this.props.element.getGraphic()?.updateGraphicInfo(graphicInfo);
        }
        this.context.editingStateManager.isControlValuesChanging = false;
        this.props.element.operatingGraphicInfo = null;
    };

    private handleControlPointMoving = (controlValues) => {
        if (!SelectedElementWrapperUtil
            .checkControlValuesSame(
                this.props.element.props.style.controlValues,
                controlValues,
            )) {
            const pathList = this.getBorderSketchManager()?.getSketchPathList(this.props.element, {
                controlValues,
            });
            mobxSet(this.props.element, 'operatingGraphicInfo', {
                controlValues,
                pathList,
            });
        }
        this.context.editingStateManager.isControlValuesChanging = true;
    };

    private handleControlPointMouseDown = () => {
        this.context.editingStateManager.isControlValuesChanging = true;
    };
}

export default SelectedImageWrapper;
